import type { HelpTopic } from '../types';

export const worksAsTopics: HelpTopic[] = [
  {
    id: 'wms.works.create-in-as',
    title: 'Create a Work (from AS page)',
    icon: 'ri-building-2-line',
    summary: 'Create a standalone work that lands in the Pending tab. Optionally start from an orphan estimate.',
    sections: [
      {
        id: 'why',
        title: 'When to use this',
        body: (
          <p className="text-sm text-gray-700">
            Use <span className="font-medium">New ▾ → Create Work</span> when a work needs to exist before an admin
            sanction is issued. Also available from the Works page's <span className="font-medium">New Work</span> button —
            both open the same form.
          </p>
        )
      },
      {
        id: 'from-estimate',
        title: 'Start from an existing estimate',
        body: (
          <div className="space-y-2 text-sm text-gray-700">
            <p>
              Only estimates that do not have a work associated are shown. Search by estimate code or name — lookups hit
              the server so you can reach any orphan estimate, not just the top 50.
            </p>
            <p>
              Selecting an estimate prefills the work name, estimated amount, plus Work Type / Category / Scheme / UDA
              when the estimate carries them. On save the estimate is relinked to the new work.
            </p>
          </div>
        )
      },
      {
        id: 'steps',
        title: 'Steps',
        body: (
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
            <li>(Optional) Pick an orphan estimate to prefill the form.</li>
            <li>Fill Work Name plus any relevant dimensions.</li>
            <li>Click <span className="font-medium">Create Work</span>. You'll land on the Pending tab showing the new row.</li>
          </ol>
        )
      }
    ],
    related: ['wms.as.create-from-unsanctioned-works', 'wms.estimates.create'],
    pageLinks: [
      { label: 'Open Admin Sanctions', to: '/app/ds-wms/admin-sanctions', icon: 'ri-government-line', hint: 'New ▾ → Create Work' },
      { label: 'Open Works', to: '/app/ds-wms/works', icon: 'ri-building-2-line' }
    ],
    launchForms: [
      { label: 'Create Work', icon: 'ri-building-2-line', formKey: 'CreateWorkForm' }
    ]
  },
  {
    id: 'wms.as.create-from-unsanctioned-works',
    title: 'Create Admin Sanction (with existing works)',
    icon: 'ri-government-line',
    summary: 'Bundle works that have no admin sanction yet into a single AS — from the Pending tab or via the in-form picker.',
    sections: [
      {
        id: 'pending-tab',
        title: 'From the Pending tab',
        body: (
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
            <li>Open <span className="font-medium">/admin-sanctions</span> → view <span className="font-medium">Pending</span>.</li>
            <li>Multi-select rows (all works are filterable by FY/Scheme/Type/UDA).</li>
            <li>Click <span className="font-medium">Create Admin Sanction</span> in the purple action bar.</li>
            <li>The form opens with rows pre-filled as "Linked existing work" — only Estimated, Sanctioned, Priority, and Remarks are editable.</li>
          </ol>
        )
      },
      {
        id: 'in-form-picker',
        title: 'From inside the form',
        body: (
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
            <li>On Step 2 (Works) click <span className="font-medium">Pick Existing</span>.</li>
            <li>Use filters (FY / Scheme / Type / UDA / Only with estimate) and search.</li>
            <li>Select works and click Add — they attach with read-only metadata.</li>
          </ol>
        )
      },
      {
        id: 'no-re-create',
        title: 'No duplicate works',
        body: (
          <p className="text-sm text-gray-700">
            Picked existing works are <span className="font-medium">not re-created</span> on save. The form updates each
            work's <code>admn_snctn_id</code> plus the four editable fields; new manual rows (without an existing work id)
            still go through work creation.
          </p>
        )
      }
    ],
    related: ['wms.works.create-in-as', 'wms.funds.source-details'],
    pageLinks: [
      { label: 'Open Admin Sanctions', to: '/app/ds-wms/admin-sanctions', icon: 'ri-government-line', hint: 'Switch view to Pending' }
    ],
    launchForms: [
      { label: 'New Admin Sanction', icon: 'ri-government-line', formKey: 'AdminSanctionForm' },
      { label: 'Create Work', icon: 'ri-building-2-line', formKey: 'CreateWorkForm' }
    ]
  },
  {
    id: 'wms.funds.source-details',
    title: 'Fund source details',
    icon: 'ri-money-rupee-circle-line',
    summary: 'Funding pattern per work — CAPEX / OPEX split, and the sanction authority cap check.',
    sections: [
      {
        id: 'where',
        title: 'Where to set it',
        body: (
          <p className="text-sm text-gray-700">
            Admin Sanction form → Step 3 (<span className="font-medium">Funding Pattern</span>). Each work in the AS can
            split its sanctioned amount across sources. Totals roll up to the AS header.
          </p>
        ),
      },
      {
        id: 'authorities',
        title: 'Sanction authority',
        body: (
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
            <li>Pick the authority on Step 1 (Sanction Details).</li>
            <li>Authorities carry min/max amount limits — pick one whose cap covers the AS total.</li>
            <li>Current options: State Govt MA&UD, PS MA&UD, CDMA, UDA Commissioner, EnC (PHED).</li>
          </ul>
        )
      }
    ],
    pageLinks: [
      { label: 'Open Admin Sanctions', to: '/app/ds-wms/admin-sanctions', icon: 'ri-government-line' }
    ]
  }
];
